import { v4 as uuidv4 } from "uuid";
import {
  JsaBasicInfo,
  JsaRacInfo,
  JsaTrainingInfo,
  JsaNonEmployeeSignaturesInfo,
  JsaEmployeeSignaturesInfo,
} from "./core";
import {
  JsaEmergencyEquipment,
  JsaPermitInfo,
  JsaPpeInfo,
  JsaHazCom,
  JsaConfinedSpace,
  JsaFallProtection,
} from "./v1";
// JsaV1
export class JsaV1 {
  constructor(data = {}) {
    this.id = data?.basic_info?.id || uuidv4();
    this.jsaBasicInfo = new JsaBasicInfo(data.basic_info || {});
    if (data?.basic_info && typeof data.basic_info === "object") {
      this.jsaBasicInfo.id = data.basic_info.id || this.jsaBasicInfo.id;
    }
    // Setting up the Core Jsa Component information.
    this.jsaRacInfo = new JsaRacInfo(data.hazard_rac_assessment || {});
    this.jsaTrainingInfo = new JsaTrainingInfo(data.training || {});
    this.jsaNonEmployeeSignaturesInfo = data.non_employee_signatures
      ? data.non_employee_signatures.map(
          (signature) => new JsaNonEmployeeSignaturesInfo(signature)
        )
      : [];
    this.jsaEmployeeSignaturesInfo = data.signatures
      ? data.signatures.map(
          (signature) => new JsaEmployeeSignaturesInfo(signature)
        )
      : [];

    // These are the specific sections for JSA V1
    this.jsaEmergencyEquipment = new JsaEmergencyEquipment(
      data.emergency_equipment || {}
    );
    this.jsaPermitInfo = new JsaPermitInfo(data.permit_info || {});
    this.jsaPpeInfo = new JsaPpeInfo(data.ppe || {});

    this.jsaHazCom = new JsaHazCom(data.hazcom || {});
    this.jsaConfinedSpace = new JsaConfinedSpace(data.confined_space || {});
    this.jsaFallProtection = new JsaFallProtection(data.fall_protection || {});
  }

  /**
   * Creates a deep clone of the JsaV1 instance.
   * @returns {JsaV1} A new JsaV1 instance.
   */
  clone() {
    return new JsaV1(this.toJson());
  }

  /**
   * Applies a patch received from a websocket broadcast.
   * This method ensures immutability by cloning the JSA before applying changes.
   * @param {object} patch The patch data from the server.
   * @returns {JsaV1} A new, updated JsaV1 instance.
   */
  applyPatch(patch) {
    const newJsa = this.clone();
    const { updated_fields } = patch;

    // updated_fields is an object where keys are the model property names (e.g., "jsaBasicInfo")
    // and values are objects of the fields to update.
    for (const modelKey in updated_fields) {
      if (Object.hasOwnProperty.call(newJsa, modelKey)) {
        const fieldsToUpdate = updated_fields[modelKey];
        for (const fieldKey in fieldsToUpdate) {
          // The setter pattern is `updateAttribute` or `update<FieldName>` on sub-models.
          // We prefer direct assignment on the cloned sub-object for immutability.
          if (
            Object.hasOwnProperty.call(newJsa[modelKey], fieldKey) ||
            typeof newJsa[modelKey][fieldKey] !== "undefined"
          ) {
            newJsa[modelKey][fieldKey] = fieldsToUpdate[fieldKey];
          }
        }
      }
    }

    return newJsa;
  }

  // Ewi Employee Signature rows.
  // Adding a blank row to the JSA employee signatures info
  addBlankJsaEmployeeSignaturesInfoRow() {
    const newRow = new JsaEmployeeSignaturesInfo();
    this.jsaEmployeeSignaturesInfo.push(newRow);
    return newRow;
  }

  // Adding all delData employees to the JSA employee signatures info
  addAllEmployeesToJsaEmployeeSignaturesInfo(employees) {
    // Setting up the existing employee ids in a set to filter out duplicates.
    const existingEmployeeIds = new Set(
      this.jsaEmployeeSignaturesInfo.map((emp) => emp.employeeId)
    );
    // Filter del.employees to only get the ones not already in our list
    const newEmployees = employees.filter(
      (employee) => !existingEmployeeIds.has(employee.employeeId)
    );

    // Add only the new employees while preserving all existing ones
    if (newEmployees.length > 0) {
      const newRows = newEmployees.map(
        (employee) =>
          new JsaEmployeeSignaturesInfo({
            employee_id: employee.employeeId,
            employee_name: employee.employeeName,
          })
      );
      this.jsaEmployeeSignaturesInfo = [
        ...this.jsaEmployeeSignaturesInfo,
        ...newRows,
      ];
      return newRows;
    }
    return [];
  }

  // Removing a row from the JSA employee signatures info
  removeJsaEmployeeSignaturesInfoRow(rowId) {
    this.jsaEmployeeSignaturesInfo = this.jsaEmployeeSignaturesInfo.filter(
      (row) => row.rowId !== rowId
    );
  }

  // Updating an employee signature row, data should come in as an object with employee_id and employee_name, and rowId
  updateJsaSignatureSelectedEmployee(employee) {
    const row = this.jsaEmployeeSignaturesInfo.find(
      (row) => row.rowId === employee.rowId
    );
    if (row) {
      row.updateSelectedEmployee(employee);
    }
    return row;
  }

  // Updating the signature
  updateJsaSignatureEmployeeSignature(signature, employeeRowId) {
    const row = this.jsaEmployeeSignaturesInfo.find(
      (row) => row.rowId === employeeRowId
    );
    if (row) {
      row.updateSignature(signature);
    }
    return row;
  }

  // Non-employee signature functions
  // Updating the non-employee name
  updateJsaSignatureNonEmployeeName(name, nonEmployeeRowId) {
    const row = this.jsaNonEmployeeSignaturesInfo.find(
      (row) => row.rowId === nonEmployeeRowId
    );
    if (row) {
      row.updateNonEmployeeName(name);
    }
    return row;
  }

  // Updating the non-employee company
  updateJsaSignatureNonEmployeeCompany(company, nonEmployeeRowId) {
    const row = this.jsaNonEmployeeSignaturesInfo.find(
      (row) => row.rowId === nonEmployeeRowId
    );
    if (row) {
      row.updateNonEmployeeCompany(company);
    }
    return row;
  }

  // Updating the non-employee signature
  updateJsaSignatureNonEmployeeSignature(signature, nonEmployeeRowId) {
    const row = this.jsaNonEmployeeSignaturesInfo.find(
      (row) => row.rowId === nonEmployeeRowId
    );
    if (row) {
      row.updateNonEmployeeSignature(signature);
    }
    return row;
  }

  // Non employee signatures functions
  // Adding a blank row to the JSA non employee signatures info
  addBlankJsaNonEmployeeSignaturesInfoRow() {
    const newRow = new JsaNonEmployeeSignaturesInfo();
    this.jsaNonEmployeeSignaturesInfo.push(newRow);
    return newRow;
  }

  // Removing a row from the JSA non employee signatures info
  removeJsaNonEmployeeSignaturesInfoRow(rowId) {
    this.jsaNonEmployeeSignaturesInfo =
      this.jsaNonEmployeeSignaturesInfo.filter((row) => row.rowId !== rowId);
  }

  // To json for sending to the database
  toJson() {
    return {
      // Core Jsa Component information
      basic_info: this.jsaBasicInfo.toJson(),
      hazard_rac_assessment: this.jsaRacInfo.toJson(),
      training: this.jsaTrainingInfo.toJson(),
      training_employees: this.jsaTrainingInfo.toJsonTrainingEmployeesInfo(),
      non_ewi_signatures: this.jsaNonEmployeeSignaturesInfo.map((sig) =>
        sig.toJson()
      ),
      signatures: this.jsaEmployeeSignaturesInfo.map((sig) => sig.toJson()),
      // Specific sections for JSA V1
      emergency_equipment: this.jsaEmergencyEquipment.toJson(),
      permit_info: this.jsaPermitInfo.toJson(),
      ppe: this.jsaPpeInfo.toJson(),
      hazcom: this.jsaHazCom.toJson(),
      confined_space: this.jsaConfinedSpace.toJson(),
      fall_protection: this.jsaFallProtection.toJson(),
      fall_protection_inspection:
        this.jsaFallProtection.toJsonFallProtectionInspectionList(),
    };
  }
}
